import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, FilePlus2, Upload, Sparkles, Target, FileText,
  Loader2, ShieldCheck, Clock,
} from 'lucide-react';
import { rb, AIBackground } from './theme';
import useResumeSession, { readDraft } from './useResumeSession';
import TemplatePicker from './TemplatePicker';
import WizardShell from './WizardShell';
import MyResumes from './MyResumes';

const MAX_UPLOAD_MB = 10;

function Header({ onBack }) {
  return (
    <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-white/10">
      <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white">
        <ArrowLeft className="w-4 h-4" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${rb.glowBtn}`}>
          <Sparkles className="w-4 h-4 text-white" />
        </span>
        <h1 className="text-base md:text-lg font-bold text-slate-100">AI Resume Builder</h1>
      </div>
      <div className="w-10 sm:w-24" />
    </div>
  );
}

function Teaser() {
  const navigate = useNavigate();
  const goLogin = () => {
    localStorage.setItem('postLoginRedirect', '/resume-builder');
    navigate('/login');
  };

  const perks = [
    { Icon: Clock, text: 'Ready in under 10 minutes — just answer simple questions' },
    { Icon: Sparkles, text: 'AI polishes your words so they sound professional' },
    { Icon: Target, text: 'ATS check tells you how well you match a job before you apply' },
    { Icon: FileText, text: 'Download a clean PDF, or upload your old resume to improve it' },
  ];

  return (
    <div className="max-w-lg mx-auto px-4 py-10 text-center">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-violet-200 mb-4 ${rb.glassSoft}`}>
          <Sparkles className="w-3.5 h-3.5 text-violet-300 rb-pulse" /> Free AI Resume Builder
        </span>
        <h1 className="text-2xl md:text-3xl font-bold mb-3 text-slate-100">
          Build a resume that gets you <span className="rb-grad-text">interviews</span>
        </h1>
        <p className="text-sm mb-8 text-slate-400">
          No design skills needed. Answer a few questions and download a professional, ATS-friendly PDF.
        </p>
        <div className="space-y-3 text-left mb-8">
          {perks.map(({ Icon, text }) => (
            <div key={text} className={`flex items-center gap-3 p-3.5 rounded-2xl ${rb.glass}`}>
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${rb.chip}`}>
                <Icon className="w-4 h-4 text-violet-300" />
              </span>
              <span className="text-sm text-slate-200">{text}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={goLogin} className={`w-full py-3.5 rounded-2xl text-base ${rb.glowBtn}`}>
          Login to build your resume
        </button>
        <p className="mt-3 text-xs flex items-center justify-center gap-1 text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5" /> It's free for job seekers
        </p>
      </motion.div>
    </div>
  );
}

export default function ResumeBuilderPage({ isAuthenticated }) {
  const navigate = useNavigate();
  const session = useResumeSession();
  const [view, setView] = useState('start'); // 'start' | 'template' | 'wizard'
  const [busy, setBusy] = useState(null); // 'upload' | 'draft' | 'start'
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const draft = readDraft();

  const startWithTemplate = async (templateId) => {
    setBusy('start');
    setError(null);
    try {
      await session.startFresh(templateId);
      setView('wizard');
    } catch {
      setError("Couldn't start a new resume. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(pdf|docx?|doc)$/i.test(file.name)) {
      setError('Please upload a PDF or Word (.doc/.docx) file.');
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`File is too large — maximum ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setBusy('upload');
    setError(null);
    try {
      await session.resumeFromUpload(file);
      setView('wizard');
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't read that file. You can still start fresh below.");
    } finally {
      setBusy(null);
    }
  };

  const continueDraft = async () => {
    setBusy('draft');
    setError(null);
    try {
      await session.resumeDraft();
      setView('wizard');
    } catch {
      setError("Couldn't restore your draft. Please start again.");
    } finally {
      setBusy(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={rb.pageBg}>
        <AIBackground />
        <Header onBack={() => navigate('/')} />
        <Teaser />
      </div>
    );
  }

  return (
    <div className={rb.pageBg}>
      <AIBackground />
      <Header onBack={() => (view === 'start' ? navigate('/search') : setView('start'))} />

      {view === 'start' && (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-violet-200 mb-4 ${rb.glassSoft}`}>
              <Sparkles className="w-3.5 h-3.5 text-violet-300 rb-pulse" /> Powered by AI · Free for job seekers
            </span>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 text-slate-100">
              Let's build your <span className="rb-grad-text">standout resume</span>
            </h1>
            <p className="text-sm mb-6 text-slate-400">
              Answer simple questions, let AI polish your words, and download a professional PDF.
            </p>

            {draft && (
              <button
                type="button"
                onClick={continueDraft}
                disabled={busy !== null}
                className={`w-full mb-4 flex items-center justify-between gap-3 p-4 rounded-2xl ${rb.glass} ${rb.gradRing}`}
              >
                <div className="text-left">
                  <p className="font-bold text-sm text-slate-100 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-300 rb-pulse" /> Continue your unfinished resume
                  </p>
                  <p className="text-xs text-slate-400">Saved {new Date(draft.savedAt).toLocaleString()}</p>
                </div>
                {busy === 'draft'
                  ? <Loader2 className="w-5 h-5 animate-spin text-violet-300 shrink-0" />
                  : <span className={`text-xs px-3 py-1.5 rounded-full ${rb.glowBtn}`}>Resume →</span>}
              </button>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setView('template')}
                disabled={busy !== null}
                className={`p-6 rounded-3xl text-left transition-transform hover:-translate-y-1 ${rb.glass}`}
              >
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${rb.glowBtn}`}>
                  <FilePlus2 className="w-5 h-5 text-white" />
                </span>
                <h3 className="font-bold mb-1 text-slate-100">Start fresh</h3>
                <p className="text-xs text-slate-400">Build a new resume step by step. Best if you don't have one yet.</p>
              </button>

              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy !== null}
                className={`p-6 rounded-3xl text-left transition-transform hover:-translate-y-1 ${rb.glass}`}
              >
                <span className="w-12 h-12 rounded-2xl bg-violet-500/15 border border-violet-400/30 flex items-center justify-center mb-4">
                  {busy === 'upload'
                    ? <Loader2 className="w-5 h-5 animate-spin text-violet-300" />
                    : <Upload className="w-5 h-5 text-violet-300" />}
                </span>
                <h3 className="font-bold mb-1 text-slate-100">
                  {busy === 'upload' ? 'Reading your resume…' : 'Upload existing resume'}
                </h3>
                <p className="text-xs text-slate-400">PDF or Word file — we'll fill in your details automatically.</p>
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleUpload} />
            </div>

            {error && <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-sm">{error}</div>}

            <MyResumes />
          </motion.div>
        </div>
      )}

      {view === 'template' && (
        <div className="py-6">
          <TemplatePicker onSelect={startWithTemplate} starting={busy === 'start'} />
          {error && <p className="max-w-4xl mx-auto px-4 mt-3 text-sm text-red-400">{error}</p>}
        </div>
      )}

      {view === 'wizard' && <WizardShell session={session} />}
    </div>
  );
}
